import {
  ODSTable,
  ODSTableHead,
  ODSTableHeadRow,
  ODSTableHeadCell,
  ODSTableBody,
  ODSTableRow,
  ODSTableRowCell,
  ODSTableFooter,
} from '@telekom-ods/react-ui-kit';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import useGlobalLoader from '../../hooks/useGlobalLoader';
import Alert from '../Alert';
import SpinnerLoader from '../Loaders/SpinnerLoader';

import CellComponents from './CellComponents';
import Header from './Header';
import TableStyles from './Table.module.css';
import type { DropDownSelectProps } from './utils';

import type {
  AllowSelectedHighlight,
  CreateAction,
  ExportData,
  HeaderActionSlot,
  PaginationChangeEvent,
  TableError,
  TableField,
  TableRowData,
} from '@/types/tableTypes';

export interface TableProps {
  isPaginated?: boolean;
  header?: string;
  subHeader?: string;
  fields: TableField[];
  rows?: TableRowData[];
  footerRows?: TableRowData[];
  pageSize?: number;
  setFilter?: (filter: unknown) => void;
  setAdvancedFieldFilter?: (filter: Record<string, string | string[]>) => void;
  totalItems?: number;
  createAction?: CreateAction | null;
  headerActionSlot?: HeaderActionSlot | null;
  exportData?: ExportData | null;
  onPageChange?: (page: number) => void;
  dropDownSelect?: DropDownSelectProps | null;
  search?: boolean;
  advancedSearch?: boolean;
  setItemsPerPage?: (pageSize: number) => void;
  pagination?: boolean;
  isLoading?: boolean;
  error?: TableError | string | null;
  allowSelectedHighlight?: AllowSelectedHighlight | null;
  emptyTableMessageKey?: string;
  styles?: { height?: string | number };
}

const Table = ({
  isPaginated = false,
  header = '',
  subHeader = '',
  fields,
  rows = [],
  footerRows = [],
  pageSize = 5,
  setFilter,
  setAdvancedFieldFilter,
  totalItems = 0,
  createAction = null,
  headerActionSlot = null,
  exportData = null,
  onPageChange,
  dropDownSelect = null,
  search = true,
  advancedSearch = true,
  setItemsPerPage,
  pagination = true,
  isLoading = false,
  error = null,
  allowSelectedHighlight = null,
  emptyTableMessageKey = 'emptyTable',
}: TableProps) => {
  const intl = useIntl();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchedRows, setSearchedRows] = useState<TableRowData[]>(rows);
  const [tableSize, setTableSize] = useState<number>(pageSize);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const { loading } = useGlobalLoader();
  const safeRows: TableRowData[] = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const handleRequestSort = (property: string): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const compareValues = useCallback(
    (aValue: string | number, bValue: string | number): number => {
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    },
    [order]
  );

  const getSortValue = (row: TableRowData, fieldId: string): string | number => {
    return row.data.find(item => item.field === fieldId)?.value ?? '';
  };

  const sortedRows = useMemo<TableRowData[]>(() => {
    if (!orderBy) return searchedRows;
    return [...searchedRows].sort((a, b) => {
      const aValue = getSortValue(a, orderBy);
      const bValue = getSortValue(b, orderBy);
      return compareValues(aValue, bValue);
    });
  }, [orderBy, searchedRows, compareValues]);

  const filteredRows = isPaginated ? rows : sortedRows;

  useEffect(() => {
    setSearchedRows(safeRows);
  }, [safeRows]);

  const paginatedRows = isPaginated
    ? safeRows
    : sortedRows.slice(currentPage * tableSize, currentPage * tableSize + tableSize);

  const handlePaginationChange = (e: PaginationChangeEvent): void => {
    setCurrentPage(e.page);
    onPageChange?.(e.page);
    setTableSize(e.pageSize);
    setItemsPerPage?.(e.pageSize);
  };

  useEffect(() => {
    setTableSize(pageSize);
  }, [pageSize]);

  const isRowSelected = (rowKey: string): boolean => {
    return allowSelectedHighlight?.selectedKey === rowKey;
  };

  const getClickableCursor = (): string => 'pointer';

  const getDefaultCursor = (): string => 'default';

  const getRowCursor = (onRowClick?: (row: TableRowData) => void): string => {
    return onRowClick ? getClickableCursor() : getDefaultCursor();
  };

  return (
    <>
      <Header
        showHeader={!!header}
        header={header}
        subHeader={subHeader}
        {...(setFilter && { setFilter })}
        {...(setAdvancedFieldFilter && { setAdvancedFieldFilter })}
        setCurrentPage={setCurrentPage}
        createAction={createAction}
        headerActionSlot={headerActionSlot}
        exportData={exportData}
        search={search}
        rows={safeRows}
        setSearchedRows={setSearchedRows}
        fields={fields}
        dropDownSelect={dropDownSelect}
        advancedSearch={advancedSearch}
        enablePaginatedSearch={isPaginated}
      />
      <SpinnerLoader
        isLoading={isLoading}
        labelText='Loading table data...'
      >
        <ODSTable
          pagination={pagination}
          paginationTableProps={{
            pageTotal: isPaginated ? totalItems : filteredRows.length,
            initialPageSize: tableSize,
            pageSizeOptions: [5, 10, 25, 50],
            initialPageIndex: currentPage,
            onChange: handlePaginationChange,
          }}
        >
          <ODSTableHead style={{ zIndex: 1 }}>
            <ODSTableHeadRow>
              {fields.map(field => (
                <ODSTableHeadCell
                  key={field.id}
                  label={field.title}
                  onSort={() => {
                    handleRequestSort(field.id);
                  }}
                  showSortIndicator={!isPaginated}
                />
              ))}
            </ODSTableHeadRow>
          </ODSTableHead>
          {error ? (
            <ODSTableBody>
              <ODSTableRow>
                <td colSpan={fields.length}>
                  <Alert
                    mode='error'
                    title={intl.formatMessage({
                      id: typeof error === 'string' ? error : error.message,
                    })}
                    style={{ margin: '1rem' }}
                    text=''
                  />
                </td>
              </ODSTableRow>
            </ODSTableBody>
          ) : (
            <ODSTableBody>
              {paginatedRows.length > 0 &&
                paginatedRows.map(row => (
                  <ODSTableRow
                    className={`ods-table-row-hover ${isRowSelected(row.key) ? 'selected-row' : ''}`}
                    key={row.key}
                    style={{ cursor: getRowCursor(row.onRowClick) }}
                    onClick={row.onRowClick ? () => row.onRowClick?.(row) : undefined}
                  >
                    {row.data.map(cell => (
                      <CellComponents
                        key={`${row.key}-${cell.field}`}
                        cell={cell}
                      />
                    ))}
                  </ODSTableRow>
                ))}
              {!loading && paginatedRows.length === 0 && (
                <ODSTableRow className={TableStyles.ods_table_row}>
                  <td colSpan={fields.length}>
                    <Alert
                      className={TableStyles.empty_table_alert ?? ''}
                      mode='error'
                      title={intl.formatMessage({ id: emptyTableMessageKey })}
                      text=''
                    />
                  </td>
                </ODSTableRow>
              )}
            </ODSTableBody>
          )}
          <ODSTableFooter>
            {footerRows.map(row => (
              <tr
                key={row.key}
                style={{ cursor: row.onRowClick ? 'pointer' : 'not-allowed' }}
                onClick={row.onRowClick ? () => row.onRowClick?.(row) : undefined}
              >
                {row.data.map((cell, idx) => {
                  if (idx === row.data.length - 1) {
                    return (
                      <ODSTableRowCell
                        key={`${row.key}-${cell.field}`}
                        align='right'
                        label={cell.value || 'N/A'}
                      />
                    );
                  }
                  return (
                    <ODSTableRowCell
                      key={`${row.key}-${cell.field}`}
                      label={cell.value || 'N/A'}
                    />
                  );
                })}
              </tr>
            ))}
          </ODSTableFooter>
        </ODSTable>
      </SpinnerLoader>
    </>
  );
};

export default Table;
